import { Injectable } from '@angular/core';
import { domColorSchema, domStyleVarSchema, domUrlSchema } from '@shared/ragai.schemas';
import { z } from 'zod';

/**
 * Direkter Zugriff auf DOM-Elemente und -Styles des Bot-Fensters (`RAGAI.DOM`).
 * @remarks
 * {@link body}/{@link header}/{@link content}/{@link footer} greifen auf feste, wohlbekannte Element-IDs des
 * Bot-Grundgerüsts zu (`bot-container`/`bot-header`/`bot-content`/`bot-footer`) und liefern `null`, falls das
 * jeweilige Element (noch) nicht im DOM existiert. {@link hideHeader}/{@link showHeader} arbeiten über eine
 * CSS-Klasse auf `document.body`, {@link hideFooter}/{@link showFooter} direkt über `style.display` - beide
 * Mechanismen sind rein präsentational und haben keinen Einfluss auf die Bot-Logik selbst.
 */
@Injectable({
  providedIn: 'root'
})
export class DOMManagerService {
  /**
   * Gibt das Bot-Container-Element zurück.
   * @returns Das Container-Element, oder `null`.
   */
  public body() { return document.getElementById("bot-container"); }
  /**
   * Gibt das Bot-Header-Element zurück.
   * @returns Das Header-Element, oder `null`.
   */
  public header() { return document.getElementById("bot-header"); }
  /**
   * Gibt das Bot-Content-Element zurück.
   * @returns Das Content-Element, oder `null`.
   */
  public content() { return document.getElementById("bot-content"); }
  /**
   * Gibt das Bot-Footer-Element zurück.
   * @returns Das Footer-Element, oder `null`.
   */
  public footer() { return document.getElementById("bot-footer"); }

  /** Blendet den Bot-Footer aus. */
  public hideFooter() {
    const f = document.getElementById("bot-footer");
    if (f) {
      f.style.display = "none";
    }
  }

  /** Blendet den Bot-Footer ein. */
  public showFooter() {
    const f = document.getElementById("bot-footer");
    if (f) {
      f.style.display = "block";
    }
  }
  /** Blendet den Bot-Header aus. */
  public hideHeader() {
    document.body.classList.add('header-hidden');
  }

  /** Blendet den Bot-Header ein. */
  public showHeader() {
    document.body.classList.remove('header-hidden');
  }

  /**
   * Erstellt ein DOM-Element aus einem HTML-String.
   * @param html Der HTML-String.
   * @returns Das erzeugte Element.
   */
  public createElementFromHTML(html: string): HTMLElement {
    const template = document.createElement('template');
    template.innerHTML = html.trim(); // trim nötig, sonst TextNodes
    return template.content.firstElementChild as HTMLElement;
  }

  /**
   * Setzt eine CSS-Custom-Property auf allen Elementen des Bots.
   * @param key Name der CSS-Custom-Property, oder ein Objekt gemäß `domStyleVarSchema`.
   * @param value Der zu setzende Wert.
   */
  public styleVar(key: string | z.input<typeof domStyleVarSchema>, value?: string): void {
    const parsed = domStyleVarSchema.safeParse(key);
    if (parsed.success && typeof key === 'object') {
      value = parsed.data.value;
      key = parsed.data.key;
    }
    document.querySelectorAll<HTMLElement>('*').forEach(el => {
      el.style.setProperty(key as string, value ?? '');
    });
  }

  /**
   * Lädt ein externes JavaScript dynamisch nach.
   * @param url URL des Skripts, oder ein Objekt gemäß `domUrlSchema`.
   * @returns Nichts, sobald das Skript geladen wurde.
   */
  public async loadExternalJS(url: string | z.input<typeof domUrlSchema>): Promise<void> {
    const parsed = domUrlSchema.safeParse(url);
    if (parsed.success && typeof url === 'object') {
      url = parsed.data.url;
    }
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = url as string;
      script.type = 'text/javascript';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load script: ${url as string}`));
      document.head.appendChild(script);
    });
  }

  /**
   * Lädt ein externes Stylesheet dynamisch nach.
   * @param url URL des Stylesheets, oder ein Objekt gemäß `domUrlSchema`.
   * @returns Nichts, sobald das Stylesheet geladen wurde.
   */
  public async loadExternalCSS(url: string | z.input<typeof domUrlSchema>): Promise<void> {
    const parsed = domUrlSchema.safeParse(url);
    if (parsed.success && typeof url === 'object') {
      url = parsed.data.url;
    }
    return new Promise((resolve, reject) => {
      const link = document.createElement('link');
      link.href = url as string;
      link.rel = 'stylesheet';
      link.type = 'text/css';
      link.onload = () => resolve();
      link.onerror = () => reject(new Error(`Failed to load CSS: ${url as string}`));
      document.head.appendChild(link);
    });
  }

  /**
   * Setzt die Hintergrundfarbe des Bot-Containers.
   * @param color Die zu setzende Farbe, oder ein Objekt gemäß `domColorSchema`.
   */
  public setBodyColor(color: string | z.input<typeof domColorSchema>): void {
    const parsed = domColorSchema.safeParse(color);
    if (parsed.success && typeof color === 'object') {
      color = parsed.data.color;
    }
    const body = document.getElementById("bot-container");
    if (body) {
      body.style.backgroundColor = color as string;
    }
  }
}
